<script setup>
import { formatCurrency, formatNumber, formatPercent } from '../Utils/numberFormat.js'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import axios from 'axios'
import StatChart from './StatChart.vue'
import ChartSkeleton from './ChartSkeleton.vue'
import CenterRevenueObjectives from './CenterRevenueObjectives.vue'
import { userIsAdmin } from '../Utils/userRoleUtils.js'
import { useHttpErrorsHandler } from '../Composables/httpErrorsHandler.js'

const props = defineProps({
    centerId: { type: Number, default: null },
    from: { type: String, default: null },
    to: { type: String, default: null },
})

const { httpErrorsHandler } = useHttpErrorsHandler()

const REVENUE_HELP = "Somme des prix des demandes gagnées sur la période, datées au mois du gain, mise à jour chaque nuit. Le prix n'est pas renseigné sur toutes les demandes : ce montant est donc un plancher."
const OBJECTIVE_HELP = "Somme des objectifs mensuels des centres sur la période. Un mois sans objectif saisi reprend le dernier objectif fixé pour le centre (fiche centre → Objectifs)."
const PROGRESS_HELP = "CA réalisé sur les mois dotés d'un objectif, rapporté à la somme de ces objectifs. Sur le mois en cours, l'avancement « attendu à date » compte ce mois au prorata des jours écoulés."
const PACE_HELP = "Atteint : objectif de la période dépassé. En avance / proche : au-dessus de l'attendu à date, ou à moins de 20 % en dessous. En retard : plus loin. Non atteint : période terminée sous l'objectif."

const STATUSES = {
    reached: { label: 'Atteint', color: 'success', icon: 'ri-trophy-line', order: 4 },
    ahead: { label: 'En avance', color: 'success', icon: 'ri-arrow-right-up-line', order: 3 },
    close: { label: 'Proche du rythme', color: 'info', icon: 'ri-arrow-right-line', order: 2 },
    behind: { label: 'En retard', color: 'warning', icon: 'ri-arrow-right-down-line', order: 1 },
    missed: { label: 'Non atteint', color: 'danger', icon: 'ri-close-circle-line', order: 0 },
}

const loading = ref(true)
const stats = ref(null)

const formatAmount = (value) => (value === null || value === undefined ? '—' : formatCurrency(value, 'EUR', { maximumFractionDigits: 0 }))
const formatGap = (value) => (value === null || value === undefined ? '—' : `${value > 0 ? '+ ' : ''}${formatAmount(value)}`)

// Filtres changés coup sur coup : seule la dernière réponse s'affiche
let latestRequest = 0

const fetchStats = () => {
    const request = ++latestRequest
    loading.value = true
    const params = {}
    if (props.centerId) params.center_id = props.centerId
    if (props.from) params.from = props.from
    if (props.to) params.to = props.to

    axios.get('/api/revenue-statistics', { params })
        .then((response) => {
            if (request === latestRequest) stats.value = response.data.data
        })
        .catch((error) => {
            if (request === latestRequest) httpErrorsHandler(error)
        })
        .finally(() => {
            if (request === latestRequest) loading.value = false
        })
}

watch(() => [props.centerId, props.from, props.to], fetchStats, { immediate: true })

const totals = computed(() => stats.value?.totals ?? {})
const inProgress = computed(() => stats.value?.in_progress ?? false)
const withoutObjective = computed(() => (totals.value.centers ?? 0) - (totals.value.with_objective ?? 0))

const statusOf = (key) => STATUSES[key] ?? null

// --- Réalisé vs objectif, mois par mois ---
const monthLabel = (period) => {
    const [year, month] = period.split('-').map(Number)
    const label = new Intl.DateTimeFormat('fr-FR', { month: 'short' }).format(new Date(year, month - 1, 1))
    return `${label} ${String(year).slice(2)}`
}

const monthlyChart = computed(() => {
    const rows = stats.value?.monthly ?? []
    return {
        series: [
            { name: 'CA des centres avec objectif', type: 'column', data: rows.map((row) => row.revenue_on_objective) },
            { name: 'Objectif', type: 'line', data: rows.map((row) => row.objective) },
        ],
        options: {
            chart: { toolbar: { show: false }, zoom: { enabled: false } },
            colors: ['#3577f1', '#f7b84b'],
            plotOptions: { bar: { columnWidth: '45%', borderRadius: 3 } },
            stroke: { width: [0, 3], curve: 'straight', dashArray: [0, 5] },
            markers: { size: [0, 4] },
            dataLabels: { enabled: false },
            xaxis: { categories: rows.map((row) => monthLabel(row.period)) },
            yaxis: { labels: { formatter: (value) => formatAmount(value) } },
            legend: { position: 'bottom' },
            grid: { borderColor: '#f1f1f1' },
            tooltip: { shared: true, intersect: false, y: { formatter: (value) => formatAmount(value) } },
            noData: { text: 'Aucune donnée sur la période' },
        },
    }
})

// --- Avancement par centre ---
// Par défaut : les centres les plus en retard en haut, ceux sans objectif en dernier
const sort = reactive({ key: 'progress', direction: 'asc' })

const sortedCenters = computed(() => [...(stats.value?.centers ?? [])].sort((a, b) => {
    if (sort.key === 'center_name') {
        const result = a.center_name.localeCompare(b.center_name)
        return sort.direction === 'asc' ? result : -result
    }

    const left = a[sort.key]
    const right = b[sort.key]
    // Sans valeur (pas d'objectif) : toujours en bas, quel que soit le sens
    if (left === null && right === null) return a.center_name.localeCompare(b.center_name)
    if (left === null) return 1
    if (right === null) return -1
    return sort.direction === 'asc' ? left - right : right - left
}))

const setSort = (key) => {
    if (sort.key === key) {
        sort.direction = sort.direction === 'asc' ? 'desc' : 'asc'
    } else {
        sort.key = key
        sort.direction = key === 'center_name' || key === 'progress' || key === 'gap' ? 'asc' : 'desc'
    }
}
// --- Raccourci admin : fixer les objectifs d'un centre sans quitter la page ---
const canManageObjectives = userIsAdmin()
const editedCenter = ref(null)
const objectivesModal = ref(null)

// Fenêtre refermée : les objectifs ont pu changer, les chiffres de l'onglet sont recalculés
const onObjectivesModalHidden = () => {
    editedCenter.value = null
    fetchStats()
}

const openObjectives = (center) => {
    editedCenter.value = center
    bootstrap.Modal.getOrCreateInstance(objectivesModal.value).show()
}

onMounted(() => objectivesModal.value?.addEventListener('hidden.bs.modal', onObjectivesModalHidden))
onBeforeUnmount(() => objectivesModal.value?.removeEventListener('hidden.bs.modal', onObjectivesModalHidden))

const sortIcon = (key) => (sort.key !== key ? 'mdi-unfold-more-horizontal text-muted' : (sort.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'))
</script>

<template>
    <div>
        <!-- KPI -->
        <div class="row">
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">CA réalisé&nbsp;<i class="mdi mdi-information-outline" v-tooltip="REVENUE_HELP"></i></p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatAmount(totals.revenue) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="text-muted fs-12 mb-0" v-if="!loading">sur {{ formatNumber(totals.centers) }} centre{{ totals.centers > 1 ? 's' : '' }}</p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-primary-subtle text-primary rounded-circle fs-2"><i class="mdi mdi-cash-multiple"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Objectifs cumulés&nbsp;<i class="mdi mdi-information-outline" v-tooltip="OBJECTIVE_HELP"></i></p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatAmount(totals.objective) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="text-muted fs-12 mb-0" v-if="!loading">
                                    {{ formatNumber(totals.with_objective) }} centre{{ totals.with_objective > 1 ? 's' : '' }} avec objectif<template v-if="withoutObjective"> · {{ formatNumber(withoutObjective) }} sans</template>
                                </p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-warning-subtle text-warning rounded-circle fs-2"><i class="mdi mdi-bullseye-arrow"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Avancement&nbsp;<i class="mdi mdi-information-outline" v-tooltip="PROGRESS_HELP"></i></p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatPercent(totals.progress) }}</span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="text-muted fs-12 mb-0" v-if="!loading && inProgress && totals.expected_progress !== null">
                                    attendu à date : {{ formatPercent(totals.expected_progress) }}
                                </p>
                                <p class="text-muted fs-12 mb-0" v-else-if="!loading && totals.progress !== null">période terminée</p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-success-subtle text-success rounded-circle fs-2"><i class="mdi mdi-progress-check"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-xl-3 col-md-6">
                <div class="card card-height-100">
                    <div class="card-body">
                        <div class="d-flex justify-content-between">
                            <div>
                                <p class="fw-medium text-muted mb-0">Centres&nbsp;<i class="mdi mdi-information-outline" v-tooltip="PACE_HELP"></i></p>
                                <h2 class="mt-2 mb-1 ff-secondary fw-semibold text-dark">
                                    <span v-if="!loading">{{ formatNumber(totals.reached + totals.on_track) }}<span class="fs-16 text-muted fw-normal"> / {{ formatNumber(totals.with_objective) }}</span></span>
                                    <span v-else class="placeholder-glow"><span class="placeholder col-6"></span></span>
                                </h2>
                                <p class="fs-12 mb-0" v-if="!loading">
                                    <span class="text-success">{{ formatNumber(totals.reached) }} atteint{{ totals.reached > 1 ? 's' : '' }}</span> ·
                                    <span class="text-info">{{ formatNumber(totals.on_track) }} dans le rythme</span> ·
                                    <span class="text-warning">{{ formatNumber(totals.behind) }} en retard</span>
                                </p>
                            </div>
                            <div class="avatar-sm flex-shrink-0">
                                <span class="avatar-title bg-info-subtle text-info rounded-circle fs-2"><i class="mdi mdi-office-building-marker-outline"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Réalisé vs objectif -->
        <div class="card">
            <div class="card-header align-items-center d-flex">
                <h4 class="card-title mb-0 flex-grow-1">
                    <i class="mdi mdi-chart-bar me-2 align-middle fs-4"></i>
                    Réalisé et objectif par mois
                </h4>
                <i class="mdi mdi-information-outline text-muted fs-16" v-tooltip="'Barres : CA des centres qui ont un objectif ce mois-là. Ligne : somme de leurs objectifs.'"></i>
            </div>
            <div class="card-body">
                <ChartSkeleton v-if="loading" :height="320" />
                <StatChart v-else type="line" :height="320" :series="monthlyChart.series" :options="monthlyChart.options" />
            </div>
        </div>

        <!-- Avancement par centre -->
        <div class="card">
            <div class="card-header align-items-center d-flex">
                <h4 class="card-title mb-0 flex-grow-1">
                    <i class="mdi mdi-format-list-bulleted me-2 align-middle fs-4"></i>
                    Avancement par centre
                </h4>
            </div>
            <div class="card-body">
                <ChartSkeleton v-if="loading" variant="horizontal" :height="300" />
                <div v-else class="table-responsive table-card centers-table">
                    <table class="table table-borderless table-hover table-nowrap align-middle mb-0">
                        <thead class="table-light position-sticky top-0">
                            <tr class="text-muted">
                                <th role="button" class="user-select-none" @click="setSort('center_name')">Centre <i class="mdi align-middle" :class="sortIcon('center_name')"></i></th>
                                <th role="button" class="text-end user-select-none" @click="setSort('revenue')">CA réalisé <i class="mdi align-middle" :class="sortIcon('revenue')"></i></th>
                                <th role="button" class="text-end user-select-none" @click="setSort('objective')">Objectif <i class="mdi align-middle" :class="sortIcon('objective')"></i></th>
                                <th role="button" class="user-select-none progress-col" @click="setSort('progress')">Avancement <i class="mdi align-middle" :class="sortIcon('progress')"></i></th>
                                <th role="button" class="text-end user-select-none" @click="setSort('gap')">Écart <i class="mdi align-middle" :class="sortIcon('gap')"></i></th>
                                <th role="button" class="text-end user-select-none" @click="setSort('projection')" v-if="inProgress">Projection <i class="mdi align-middle" :class="sortIcon('projection')"></i></th>
                                <th class="text-center">État</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="center in sortedCenters" :key="center.center_id" :class="{ 'text-muted': center.objective === null }">
                                <td class="fw-medium">{{ center.center_name }}</td>
                                <td class="text-end">{{ formatAmount(center.revenue) }}</td>
                                <td class="text-end text-nowrap">
                                    {{ formatAmount(center.objective) }}
                                    <a
                                        v-if="canManageObjectives"
                                        href="#"
                                        class="text-info ms-1"
                                        :title="`Objectifs de ${center.center_name}`"
                                        @click.prevent="openObjectives(center)"
                                    >
                                        <i class="ri-pencil-fill fs-16 align-middle"></i>
                                    </a>
                                </td>
                                <td class="progress-col">
                                    <div class="d-flex align-items-center gap-2" v-if="center.progress !== null">
                                        <div class="position-relative flex-grow-1">
                                            <div class="progress progress-sm bg-light rounded-pill">
                                                <div class="progress-bar rounded-pill" :class="'bg-' + statusOf(center.status).color" :style="{ width: Math.min(center.progress, 100) + '%' }"></div>
                                            </div>
                                            <div
                                                class="pace-marker"
                                                v-if="inProgress && center.expected_progress !== null && center.expected_progress < 100"
                                                :style="{ left: center.expected_progress + '%' }"
                                                v-tooltip="`Attendu à date : ${formatPercent(center.expected_progress)}`"
                                            ></div>
                                        </div>
                                        <span class="fw-semibold fs-13 text-end" style="min-width: 56px;">{{ formatPercent(center.progress) }}</span>
                                    </div>
                                    <span class="fs-12" v-else>Pas d'objectif</span>
                                </td>
                                <td class="text-end" :class="{ 'text-success': center.gap > 0, 'text-danger': center.gap < 0 }">{{ formatGap(center.gap) }}</td>
                                <td class="text-end" v-if="inProgress">{{ formatAmount(center.projection) }}</td>
                                <td class="text-center">
                                    <span v-if="statusOf(center.status)" class="badge fs-12" :class="`bg-${statusOf(center.status).color}-subtle text-${statusOf(center.status).color}`">
                                        <i class="align-middle me-1" :class="statusOf(center.status).icon"></i>{{ statusOf(center.status).label }}
                                    </span>
                                    <span v-else>—</span>
                                </td>
                            </tr>
                            <tr v-if="sortedCenters.length === 0">
                                <td colspan="7" class="text-center text-muted py-4">Aucun centre sur ce périmètre</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Objectifs d'un centre (admins) : même formulaire que sur la fiche centre -->
        <div class="modal fade" id="revenueObjectivesModal" tabindex="-1" aria-hidden="true" ref="objectivesModal">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="mdi mdi-bullseye-arrow me-2 align-middle"></i>Objectifs de chiffre d'affaires<span v-if="editedCenter"> · {{ editedCenter.center_name }}</span>
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
                    </div>
                    <div class="modal-body">
                        <CenterRevenueObjectives v-if="editedCenter" :key="editedCenter.center_id" :center-id="editedCenter.center_id" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
th[role="button"] {
    cursor: pointer;
}

/* 50 centres et plus : la liste défile sous un en-tête fixe */
.centers-table {
    max-height: 560px;
}

.progress-col {
    min-width: 220px;
}

.pace-marker {
    position: absolute;
    top: -4px;
    bottom: -4px;
    width: 3px;
    margin-left: -1px;
    border-radius: 2px;
    background-color: var(--vz-body-color);
    opacity: 0.55;
    cursor: help;
}
</style>
